import PasswordTextField from "@components/inputs/PasswordTextField";
import { useForgotButtonPosition } from "@configs/statsig";
import { STORAGE_KEY, ValidatorSchema } from "@constants";
import { zodResolver } from "@hookform/resolvers/zod";
import useCaptureFormValidationErrors from "@hooks/useCaptureFormValidationErrors";
import { useLoginSubmit } from "@hooks/useLoginSubmit";
import { getStoredCredential } from "@utils/credentials";
import { formatTimer } from "@utils/misc";
import { AuthToken, Button, FieldHelperText, TextField, delay, useFieldHelperText } from "kz-ui-sdk";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { MdChevronRight, MdPerson } from "react-icons/md";
import { z } from "zod";

interface CredentialFormProps {
  onLoginSuccess?: (token?: AuthToken) => void;
  onAccountLocked?: (isLocked: boolean) => void;
  isAccountLocked?: boolean;
  onForgotSecret?: () => void;
  lockedRemainingTime?: number;
}

const credentialFormSchema = z.object({
  username: ValidatorSchema.username,
  password: z.string(),
});

type CredentialFormType = z.infer<typeof credentialFormSchema>;

const inputClasses = {
  "input&": "w-full !px-0 !ps-11 text-start",
  "iconContainer&": "!start-3 !top-[10px]",
};

const getPersistedCredential = () => {
  const persistedCredential = getStoredCredential();
  return {
    username: localStorage.getItem(STORAGE_KEY.LOGIN_CRED_ID) ?? "",
    password: persistedCredential.password ?? "",
  };
};

const FormUsernamePassword = ({
  onLoginSuccess,
  onAccountLocked,
  isAccountLocked,
  onForgotSecret,
  lockedRemainingTime,
}: CredentialFormProps) => {
  const { t } = useTranslation();
  const [loginError, setLoginError] = useState<string>("");
  const [countdownTime, setCountdownTime] = useState<number>(lockedRemainingTime || 0);
  const { showProminentButton } = useForgotButtonPosition();

  const { handleLogin, isLoading: isLoginLoading } = useLoginSubmit({
    onLoginSuccess,
    onAccountLocked,
    onForgotSecret,
    setInlineError: setLoginError,
  });

  const form = useForm<CredentialFormType>({
    mode: "onChange",
    resolver: zodResolver(credentialFormSchema),
    defaultValues: getPersistedCredential(),
  });

  const {
    handleSubmit: handleFormSubmit,
    register,
    formState: { errors: formErrors, submitCount },
    watch,
    trigger,
  } = form;

  useCaptureFormValidationErrors(form, { feature: "login-username-password" });

  const fieldHelperTextManager = useFieldHelperText({
    submitCount: submitCount,
    trigger: trigger,
    errors: formErrors,
  });

  // Clear inline login error when user types
  useEffect(() => {
    const subscription = watch(() => {
      setLoginError("");
    });
    return () => subscription.unsubscribe();
  }, [watch, setLoginError]);

  // Countdown timer effect
  useEffect(() => {
    if (isAccountLocked && countdownTime > 0) {
      const timer = setInterval(() => {
        setCountdownTime((prevTime) => {
          if (prevTime <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [isAccountLocked, countdownTime]);

  // Update countdown time when lockedRemainingTime prop changes
  useEffect(() => {
    if (lockedRemainingTime !== undefined) {
      setCountdownTime(lockedRemainingTime);
    }
  }, [lockedRemainingTime]);

  const onSubmit = async (data: CredentialFormType) => {
    handleLogin(data.username, data.password);
  };

  return (
    <form
      id="form-login-username-password"
      name="form-login-username-password"
      onSubmit={handleFormSubmit((e) => delay(0).then(() => onSubmit(e)))}
      className="flex w-full flex-col gap-y-3"
      autoComplete="off"
    >
      <TextField
        {...register("username")}
        error={!!formErrors.username}
        helperText={t(formErrors.username?.message as string)}
        fieldHelperTextManager={fieldHelperTextManager}
        placeholder={t("username")}
        icon={<MdPerson className="text-2xl text-content-base" />}
        classes={inputClasses}
        size="sm"
        autoComplete="new-password"
        className="animate-fade-in"
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        aria-label="Login username input username-password"
      />
      <div>
        <PasswordTextField
          {...register("password")}
          error={!!formErrors.password}
          helperText={t(formErrors.password?.message as string)}
          placeholder={t("password")}
          size="sm"
          classes={inputClasses}
          buttonClass="!top-[10px]"
          helperTextClasses={{
            className: "whitespace-pre-line",
          }}
          // ignore browser save credential
          autoComplete="new-password"
          className="animate-fade-in"
          aria-label="Login password input username-password"
        />
      </div>
      {loginError && (
        <FieldHelperText
          error={!!loginError}
          helperText={t(loginError)}
          alwaysDisplay={true}
          t={t}
          className="my-5 !text-center"
          classes={{
            helperText: "whitespace-pre-line",
          }}
        />
      )}
      {isAccountLocked && countdownTime > 0 && (
        <FieldHelperText
          error
          helperText={t("locked due to too many wrong tries. please try again in {{timer}}.", {
            timer: formatTimer(countdownTime, {
              minutes: false,
              seconds: true,
            }),
          })}
          alwaysDisplay={true}
          t={t}
          className="my-5 !text-center"
          classes={{
            helperText: "whitespace-pre-line",
          }}
        />
      )}

      {showProminentButton ? (
        <div className="flex justify-center">
          <Button
            size="sm"
            className="mt-1 !w-fit px-3"
            variant="secondary"
            onClick={onForgotSecret}
            disabled={isAccountLocked}
            aria-label="Login forgot password username-password"
            type="button"
          >
            {t("forgot password")}
          </Button>
        </div>
      ) : (
        <div className="flex justify-end">
          <Button
            size="sm"
            variant="contained"
            iconPosition="right"
            icon={<MdChevronRight className="invert-x text-base text-content-base" />}
            type="button"
            classes={{
              "root&": "!border-none !bg-transparent !h-[16px] w-fit",
            }}
            onClick={onForgotSecret}
            disabled={isAccountLocked}
            aria-label="Login forgot password username-password"
          >
            {t("forgot password")}
          </Button>
        </div>
      )}
      <Button
        variant="primary"
        size="lg"
        className="mt-1"
        loading={isLoginLoading}
        disabled={isLoginLoading}
        aria-label="Login submit username-password"
      >
        {t("Login")}
      </Button>
    </form>
  );
};

export default FormUsernamePassword;
